import { useEffect, useState } from 'react';
import usecustomStyles from "./customStyles";
import MoneyDisplay from "./MoneyDisplay";
import WarnTriangle from "./WarnTriangle";
import BoolDisplay from "./BoolDisplay";
import {intentTypeIcons, repeatableTooltipedIcon, customStyle, darkthemecolors} from "../config.js";
import { useSelector } from "react-redux";
import utils from './utils';
import { useTheme }  from '../Common/ThemeContext';
import { APIClient } from "../helpers/api_helper";
import { Pagination } from 'antd';
import {
    Table,
    Typography,
    Tag,
    Button,
    Input,
    Modal,
    Popconfirm,
    Space,
    Tooltip
  } from "antd";

import {BranchesOutlined, EditFilled, EditOutlined, FlagFilled, FlagOutlined} from '@ant-design/icons';

const api = new APIClient();
const { TextArea } = Input;

const { Text, Link } = Typography;
const customStyles = usecustomStyles();

const OverrideIcon = ({ field, intent }) =>
  intent.manually_overridden_fields?.includes(field) ? (
    <Tooltip title="Vrednost je bila ročno spremenjena.">
      <EditOutlined style={{ color: '#1677ff', marginLeft: 4 }} />
    </Tooltip>
  ) : null;


const IntentsTable = (props) => {

  const { theme } = useTheme();

  // Status-based cell background colors (invoice/payment), per customer color coding:
  //  - is_issued true → green, false → red (invoice cell + its type-relevant date)
  //  - is_paid   true → green, false → red (payment cell + Realizirano date)
  const statusTokens = theme === 'dark' ? darkthemecolors.token : customStyle.token;
  const statusBg = (flag) =>
    flag === true ? statusTokens.colorSuccessBg
    : flag === false ? statusTokens.colorDangerBg
    : undefined;
  const issuedTooltip = (flag) => flag ? 'Račun evidentiran' : 'Račun še ni evidentiran';
  const paidTooltip = (flag) => flag ? 'Plačano' : 'Ni plačano';

  const tagsById = useSelector((state) => state.Tags.byId);
  const companiesById = useSelector((state) => state.Companies.byId);
  const personsById = useSelector((state) => state.Persons.byId);
  const projectsById = useSelector((state) => state.Projects.byId);
  const accountsById = useSelector((state) => state.Account.byId);

  const [flagModalIntent, setFlagModalIntent] = useState(null);
  const [flagModalText, setFlagModalText] = useState('');
  const [flagSaving, setFlagSaving] = useState(false);

  // Optimistic flag state: reflects a just-toggled flag instantly while the
  // server-side reload (props.onIntentChanged) is in flight. Reset whenever
  // fresh data arrives, so the server stays the source of truth.
  const [flagOverrides, setFlagOverrides] = useState({});
  useEffect(() => { setFlagOverrides({}); }, [props.intentsData]);
  const isFlaggedOf = (r) => flagOverrides[r.id] ?? r.is_flagged;

  const closeFlagModal = () => {
    setFlagModalIntent(null);
    setFlagModalText('');
    setFlagSaving(false);
  };

  const handleFlagSave = () => {
    const text = flagModalText.trim();
    if (!text || !flagModalIntent) return;
    const id = flagModalIntent.id;
    setFlagSaving(true);
    api
      .create(`/api/intents/${id}/comments/`, { text }, { successMsg: null })
      .then(() =>
        api.update(`/api/intents/${id}/`, { is_flagged: true }, { successMsg: null })
      )
      .then(() => {
        setFlagOverrides((prev) => ({ ...prev, [id]: true }));
        closeFlagModal();
        props.onIntentChanged?.();
      })
      .catch((err) => {
        console.error('Flag shortcut error:', err);
        setFlagSaving(false);
      });
  };

  const handleUnflag = (intent) => {
    api
      .update(`/api/intents/${intent.id}/`, { is_flagged: false }, { successMsg: null })
      .then(() => {
        setFlagOverrides((prev) => ({ ...prev, [intent.id]: false }));
        props.onIntentChanged?.();
      })
      .catch((err) => console.error('Unflag error:', err));
  };

  const columns = [
    // {title: '', dataIndex: 'id', render: id => <Text>#{id}</Text>},
    {title: 'Tip', dataIndex: 'type', fixed: 'left', render: (type, record) => (
      <>
        <Tooltip title={type}>{intentTypeIcons[type]}</Tooltip>
        {record.split_percent != null && (
          <Tooltip title="Transakcija razdeljena med projekte">
            <BranchesOutlined style={{ marginLeft: 6, color: '#1677ff' }} />
          </Tooltip>
        )}
        {record.wizard_type == 'PERIODIC' ? <span style={{ marginLeft: "6px"}}>{repeatableTooltipedIcon}</span> : null}
      </>
    )},
    {title: 'Status', key: 'status', fixed: 'left', render: (_, r) => (
      <Space size={4}>
        {r.warning && <WarnTriangle tooltip={r.warning}/>}
        {r.type === 'TAX' && r.manually_overridden_fields?.includes('amount') && (
          <Tooltip title="Davčni znesek je ročno nastavljen (zaklenjen).">
            <EditOutlined style={{ color: '#1677ff' }} />
          </Tooltip>
        )}
        {isFlaggedOf(r) && (
          <Tooltip title="Označeno – potrebuje pozornost">
            <FlagFilled style={{color:'#ff4d4f'}}/>
          </Tooltip>
        )}
      </Space>
    )},
    {title: 'Naziv', dataIndex: 'title', fixed: 'left', width: 240, ellipsis: { showTitle: false }, render: (title, intent) => (
      <Tooltip title={title}>
        <Text>
          {title}
          {intent.split_percent != null && (
            <Text type="secondary"> ({Number(intent.split_percent)}%)</Text>
          )}
          <OverrideIcon field="title" intent={intent} />
        </Text>
      </Tooltip>
    )},
    {title: 'Podjetje', dataIndex: 'company', width: 160, ellipsis: { showTitle: false }, render: cId => {
      return <Tooltip title={companiesById[cId]?.title}><Text>{companiesById[cId]?.title}</Text></Tooltip>
    }},
    {title: 'Oseba', dataIndex: 'person', width: 160, ellipsis: { showTitle: false }, render: pId => {
      return <Tooltip title={personsById[pId]?.title}><Text>{personsById[pId]?.title}</Text></Tooltip>
    }},
    {title: 'Projekt', dataIndex: 'project', width: 160, ellipsis: { showTitle: false }, render: projId => {
      return <Tooltip title={projectsById[projId]?.title}><Text>{projectsById[projId]?.title}</Text></Tooltip>
    }},
    {title: 'Račun', dataIndex: 'account', width: 160, ellipsis: { showTitle: false }, render: accId => {
      return <Tooltip title={accountsById[accId]?.title}><Text>{accountsById[accId]?.title}</Text></Tooltip>
    }},
    {title: 'Plačano', dataIndex: 'is_paid',
      onCell: r => ({ style: { backgroundColor: statusBg(r.is_paid) } }),
      render: x => <Tooltip title={paidTooltip(x)}><span><BoolDisplay val={x} /></span></Tooltip>},
    {title: 'Izdano / Prejeto', dataIndex: 'is_issued',
      onCell: r => ({ style: { backgroundColor: statusBg(r.is_issued) } }),
      render: x => <Tooltip title={issuedTooltip(x)}><span><BoolDisplay val={x} /></span></Tooltip>},
    {title: 'Prejeto', dataIndex: 'date_invoice_recieved',
      onCell: r => r.type === 'EXPENSE' ? { style: { backgroundColor: statusBg(r.is_issued) } } : {},
      render: (dt, intent) => {
        const content = <Text>{utils.formatDt(dt)}<OverrideIcon field="date_invoice_recieved" intent={intent} /></Text>;
        return intent.type === 'EXPENSE' ? <Tooltip title={issuedTooltip(intent.is_issued)}>{content}</Tooltip> : content;
      }},
    {title: 'Opravljeno', dataIndex: 'date_service_performed',
      onCell: r => r.type === 'INCOME' ? { style: { backgroundColor: statusBg(r.is_issued) } } : {},
      render: (dt, intent) => {
        const content = <Text>{utils.formatDt(dt)}<OverrideIcon field="date_service_performed" intent={intent} /></Text>;
        return intent.type === 'INCOME' ? <Tooltip title={issuedTooltip(intent.is_issued)}>{content}</Tooltip> : content;
      }},
    {title: 'Realizirano', dataIndex: 'date_to_be_paid',
      onCell: r => ({ style: { backgroundColor: statusBg(r.is_paid) } }),
      render: (dt, intent) => <Tooltip title={paidTooltip(intent.is_paid)}><Text>{utils.formatDt(dt)}<OverrideIcon field="date_to_be_paid" intent={intent} /></Text></Tooltip>},
    {title: 'Znesek brez DDV', dataIndex: 'amount_no_tax', render: (amount_no_tax, intent) => <><MoneyDisplay type={intent.type} val={amount_no_tax} /><OverrideIcon field="amount_no_tax" intent={intent} /></>},
    {title: 'Znesek z DDV', dataIndex: 'amount', render: (amount, intent) => <><MoneyDisplay type={intent.type} val={amount} /><OverrideIcon field="amount" intent={intent} /></>},
    {title: 'Davčna stopnja', dataIndex: 'tax_rate', render: (tam, intent) => <Text><small>{tam} %</small><OverrideIcon field="tax_rate" intent={intent} /></Text>},
    {title: 'Oznake', dataIndex: 'tag', render: tId => <Tag color={tagsById[tId]?.color_hex}>{tagsById[tId]?.title}</Tag>},
    {title: 'Uredi', key: 'edit', fixed: 'right', render: r => <Button size='small' onClick={ev => props.onIntentEdit(r)}><EditFilled/></Button>},
    {title: 'Označi', key: 'flag', fixed: 'right', render: r => (
      isFlaggedOf(r) ? (
        <Popconfirm
          title="Odznači to transakcijo?"
          okText="Odznači"
          cancelText="Prekliči"
          onConfirm={() => handleUnflag(r)}
        >
          <Tooltip title="Odznači">
            <Button size='small' danger><FlagFilled style={{color:'#ff4d4f'}}/></Button>
          </Tooltip>
        </Popconfirm>
      ) : (
        <Tooltip title="Označi in dodaj komentar">
          <Button size='small' onClick={() => setFlagModalIntent(r)}><FlagOutlined/></Button>
        </Tooltip>
      )
    )}
  ]

  return (
    <>

      <Table
        size="small"
        scroll={{ x: 'max-content' }}
        loading={props.loading}
        columns={columns}
        dataSource={props.intentsData?.results || []}
        pagination={false}
        rowKey={intent => intent.id}
        rowSelection={{
          fixed: true,
          onChange: (selectedIds) => {
            props.setSelectedIds(selectedIds)
          }
        }}
        rowClassName={(record) => {
          if (record.warning){
            return theme === 'dark' ? 'row-warning-dark' : 'row-warning'
          }
        }}
      />
      {!props.hidePagination && (
        <Pagination
          current={props.page}
          onChange={pInx => props.onPageChange(pInx)}
          showSizeChanger={false}
          {...utils.processPagination(props.intentsData?.pagination)}
        />
      )}

      <Modal
        title="Označi transakcijo"
        open={!!flagModalIntent}
        onCancel={closeFlagModal}
        onOk={handleFlagSave}
        okText="Označi"
        cancelText="Prekliči"
        okButtonProps={{ disabled: !flagModalText.trim() || flagSaving, loading: flagSaving }}
        destroyOnClose
      >
        <p style={{ marginTop: 0 }}>Vpiši prvi komentar – pojasni, zakaj transakcija potrebuje pozornost.</p>
        <TextArea
          value={flagModalText}
          onChange={(e) => setFlagModalText(e.target.value)}
          autoFocus
          autoSize={{ minRows: 3, maxRows: 8 }}
          placeholder="Komentar…"
        />
      </Modal>
    </>
  )
}

export default IntentsTable;
